<template>
  <a-modal title="Rate History" :visible="visible" :destroy-on-close="true" @cancel="closeModal">
    <rate-modal :visible.sync="visibleRateModal" :record.sync="record" @update="afterReset" />
    <a-modal
      v-if="rollbackRecord"
      :visible="!!rollbackRecord"
      title="Rollback change action form?"
      ok-text="Rollback rate"
      cancel-text="Cancel"
      :confirm-loading="rollingBack"
      :ok-button-props="{ disabled: !trimmedRollbackReason }"
      @cancel="closeRollback"
      @ok="submitRollback"
    >
      <p>
        A new rate-history entry will restore the previous primary rate. The existing CAF record will remain in history.
      </p>
      <a-form-model :model="{ reason: rollbackReason }">
        <a-form-model-item label="Reason" required>
          <a-textarea v-model="rollbackReason" :auto-size="{ minRows: 3, maxRows: 6 }" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <a-list item-layout="vertical" :bordered="true" :data-source="enrichedData" :loading="loading">
      <a-list-item slot="renderItem" slot-scope="item" class="flex flex-column gap-2">
        <div class="flex justify-content-between">
          <template>
            <h3 v-if="item.author">{{ item.author.first_name }} {{ item.author.last_name }}</h3>
            <h3 v-else>Initial</h3>
          </template>
          <span>{{ formatDate(item.created_at) }}</span>
        </div>

        <p>Reason: {{ item.reason }}</p>
        <p>Applied At: {{ formatDate(item.applied_at) }}</p>
        <p v-if="item.caf">CAF: {{ formatCafStatus(item.caf.status) }}</p>

        <template v-if="item.changes.length"
          ><b>Changes:</b>

          <div class="flex flex-column gap-2">
            <span v-for="field in item.changes" :key="field.label">
              <span>{{ field.label }}: </span>
              <b>{{ field.value }}</b>
            </span>
          </div></template
        >

        <a-button
          class="ml-auto w-fit p-0 flex gap-2 align-items-center"
          type="link"
          v-if="
            isLegacyResetAvailable(item) &&
            hasUncontrolledPermission('manage_associates', {
              id: associate.id
            })
          "
          @click="handleReset(item)"
        >
          Reset</a-button
        >
        <a-button
          class="ml-auto w-fit p-0 flex gap-2 align-items-center"
          type="link"
          v-if="
            isCafRollbackAvailable(item) &&
            hasUncontrolledPermission('manage_associates', {
              id: associate.id
            })
          "
          @click="openRollback(item)"
        >
          Rollback</a-button
        >
      </a-list-item>
    </a-list>
    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal">Cancel</a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { PAYMENT_METHOD } from '@/store/modules/payment/constants'
import util from '@/util'
import { Button, FormModel, Input, List, Modal, notification } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import RateModal from './rate-modal.vue'
import permissionMixin from '@/mixins/permissionMixin'
import moment from 'moment-timezone'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')

const RATE_FIELDS = [
  {
    key: 'method',
    label: 'Method',
    format: v => PAYMENT_METHOD[v] ?? v
  },
  {
    key: 'hourly_rate',
    label: 'Hourly Rate',
    format: v => `$${(v / 100).toFixed(2)}`
  },
  {
    key: 'salary',
    label: 'Salary',
    format: v => `$${(v / 100).toFixed(2)}`
  },
  {
    key: 'training_rate',
    label: 'Training Rate',
    format: v => `$${(v / 100).toFixed(2)}`
  },
  {
    key: 'orientation_rate',
    label: 'Orientation Rate',
    format: v => `$${(v / 100).toFixed(2)}`
  }
]

export default {
  components: {
    'a-modal': Modal,
    'a-list': List,
    'a-list-item': List.Item,
    'a-button': Button,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-textarea': Input.TextArea,
    'rate-modal': RateModal
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  mixins: [permissionMixin],
  data() {
    return {
      loading: false,

      data: [],

      record: undefined,

      visibleRateModal: false,
      rollbackRecord: undefined,
      rollbackReason: '',
      rollingBack: false
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          this.loadData()
        }
      },
      immediate: true
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),

    enrichedData() {
      return this.data.map((item, index) => {
        const prev = this.data[index + 1]
        const changes = this.getDisplayFields(prev, item)
        return { ...item, changes }
      })
    },

    trimmedRollbackReason() {
      return this.rollbackReason.trim()
    }
  },
  methods: {
    ...profileActions(['getRateHistory', 'reloadAssociate', 'rollbackRateHistory']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    getDisplayFields(prev, current) {
      if (prev) {
        return RATE_FIELDS.map(({ key, label, format }) => ({
          label,
          value: `${format(prev[key])} → ${format(current[key])}`
        }))
      } else {
        return RATE_FIELDS.map(({ key, label, format }) => ({
          label,
          value: format(current[key])
        }))
      }
    },

    isResetAvailable(item) {
      const { author, applied_at, created_at, history_id, id, reason, caf, ...current } = this.enrichedData[0]

      const {
        author: itemAuthor,
        applied_at: itemAppliedAt,
        created_at: itemCreatedAt,
        history_id: itemHistoryId,
        id: itemId,
        reason: itemReason,
        caf: itemCaf,
        ...itemCurrent
      } = item

      return JSON.stringify(current) !== JSON.stringify(itemCurrent)
    },

    isLegacyResetAvailable(item) {
      return !item.caf && this.isResetAvailable(item)
    },

    isCafRollbackAvailable(item) {
      return !!item.caf?.can_rollback
    },

    formatCafStatus(status) {
      const titles = {
        pending: 'Pending signature',
        signed: 'Signed',
        canceled: 'Canceled'
      }

      return titles[status] || status || '—'
    },

    handleReset(record) {
      this.visibleRateModal = true
      this.record = record
    },

    openRollback(record) {
      this.rollbackRecord = record
      this.rollbackReason = ''
    },

    closeRollback() {
      if (this.rollingBack) return

      this.rollbackRecord = undefined
      this.rollbackReason = ''
    },

    async submitRollback() {
      if (!this.rollbackRecord || !this.trimmedRollbackReason || this.rollingBack) return

      this.rollingBack = true
      let successful = false
      let invalidated = false

      try {
        await this.rollbackRateHistory({
          associate_id: this.associate.id,
          history_id: this.rollbackRecord.history_id,
          reason: this.trimmedRollbackReason
        })
        successful = true
        await this.reloadAssociate(true)
        this.showNotification('success', 'Success', 'Rate has been rolled back successfully.')
      } catch (error) {
        invalidated = error?.status === 409
        this.showNotification(
          'error',
          'Error',
          error?.data?.msg ?? error?.data?.detail ?? (typeof error === 'string' ? error : error?.message)
        )
      } finally {
        await this.loadData()
        this.rollingBack = false
        if (successful || invalidated) {
          this.closeRollback()
        }
      }
    },

    afterReset(record) {
      this.data = [record, ...this.data]
    },

    closeModal() {
      this.$emit('update:visible', false)
    },

    formatDate(date) {
      return util.convertDateTimeToCurrentTimeZoneWithFormat(date, 'MM/DD/YYYY')
    },
    convertToServerFormat(date) {
      return moment(date).startOf('day').utc().toISOString()
    },

    async loadData() {
      this.loading = true
      try {
        this.data = await this.getRateHistory({
          associate_id: this.associate.id
        })
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
::v-deep .ant-list-items {
  overflow: auto;
  max-height: 500px;
}

::v-deep .ant-spin-nested-loading {
  min-height: 100px;
}
</style>
